import { createAsyncThunk } from "@reduxjs/toolkit";
import { customFetch } from "../lib/network/Axios";
import {
    mapShipmentFromAPI,
    mapFiltersToAPI,
    mapShipmentToAPI
} from "../utils/shipmentMapper";
import {
    ShipmentDetail,
    ShipmentFilter,
    ShipmentHistoryItem,
    ShipmentListResponse
} from "../types/shipment.types";
import { handleError } from "../lib/ErrorHandler";
import { HttpStatusCode } from "axios";

const api = ''; // Base API URL is empty since the endpoint is just /shipments

/**
 * Fetch all shipments with filters
 */
export const getAllShipments = createAsyncThunk(
    "shipment/getAll",
    async (filter: ShipmentFilter = {}) => {
        try {
            // Convert the filter object to URL parameters
            const apiFilters = mapFiltersToAPI(filter);

            // Create query params object
            const queryParams = new URLSearchParams();

            // Add each filter to the query parameters
            Object.entries(apiFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value.toString());
                }
            });

            // Build the URL ensuring the ? is properly added
            const queryString = queryParams.toString();
            const url = `${api}/shipments${queryString ? `?${queryString}` : ''}`;

            console.log('Fetching shipments with filters:', filter);
            console.log('API filters transformed to:', apiFilters);
            console.log('Final URL:', url);

            const response = await customFetch.get(url);
            if (response?.status === HttpStatusCode.Ok) {
                // Check response format - could be array or object with data property
                const shipmentData = Array.isArray(response.data) ? response.data :
                    (response.data?.data && Array.isArray(response.data.data)) ? response.data.data : [];

                console.log('Shipment data:', shipmentData);

                // Map API response to our UI model
                const shipments: ShipmentDetail[] = shipmentData.map((shipment: any) => mapShipmentFromAPI(shipment));

                return {
                    shipments,
                    pagination: response.data?.pagination || {
                        page: 1,
                        limit: 50,
                        total: shipments.length
                    }
                };
            }
            return {
                shipments: [],
                pagination: { page: 1, limit: 50, total: 0 }
            };
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);

/**
 * Get a specific shipment by ID
 */
export const getShipmentById = createAsyncThunk(
    "shipment/getById",
    async (shipmentId: number) => {
        try {
            const response = await customFetch.get(`${api}/shipments/${shipmentId}`);
            if (response?.status === HttpStatusCode.Ok) {
                return mapShipmentFromAPI(response.data);
            }
            throw new Error("Shipment not found");
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);

/**
 * Create a new shipment
 */
export const createShipment = createAsyncThunk(
    "shipment/create",
    async (shipment: Partial<ShipmentDetail>) => {
        try {
            // Convert our UI model to the API expected format
            const apiPayload = mapShipmentToAPI(shipment);

            const response = await customFetch.post(`${api}/shipments`, apiPayload);
            if (response?.status === HttpStatusCode.Created || response?.status === HttpStatusCode.Ok) {
                return mapShipmentFromAPI(response.data);
            }
            throw new Error("Failed to create shipment");
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);

/**
 * Update an existing shipment
 */
export const updateShipment = createAsyncThunk(
    "shipment/update",
    async (shipment: Partial<ShipmentDetail>) => {
        try {
            if (!shipment.id) {
                throw new Error("Shipment ID is required for update");
            }

            // Convert our UI model to the API expected format
            const apiPayload = mapShipmentToAPI(shipment);

            const response = await customFetch.patch(`${api}/shipments/${shipment.id}`, apiPayload);
            if (response?.status === HttpStatusCode.Ok) {
                return mapShipmentFromAPI(response.data);
            }
            throw new Error("Failed to update shipment");
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);

/**
 * Delete a shipment
 */
export const deleteShipment = createAsyncThunk(
    "shipment/delete",
    async (shipmentId: number) => {
        try {
            const response = await customFetch.delete(`${api}/shipments/${shipmentId}`);
            if (response?.status === HttpStatusCode.Ok || response?.status === HttpStatusCode.NoContent) {
                return shipmentId;
            }
            throw new Error("Failed to delete shipment");
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);

/**
 * Get shipment history
 */
export const getShipmentHistory = createAsyncThunk(
    "shipment/getHistory",
    async (shipmentId: number): Promise<ShipmentHistoryItem[]> => {
        try {
            const response = await customFetch.get(`${api}/shipment-history/${shipmentId}`);
            if (response?.status === HttpStatusCode.Ok) {
                return response.data;
            }
            return [];
        } catch (error) {
            handleError(error);
            throw new Error((error as Error).message);
        }
    }
);
